// Canonical event taxonomy shared by lf_fe, test-yted, and the marketing
// snapshot. Sourced from docs/design-google-analytics.md §F.

export const EVENT_NAMES = Object.freeze([
    'account_deleted',
    'account_deletion_requested',
    'api_error',
    'checkout_started',
    'child_account_created',
    'child_switched',
    'coppa_consent_granted',
    'coppa_consent_revoked',
    'dashboard_viewed',
    'email_link_clicked',
    'email_opened',
    'email_sent',
    'feature_blocked',
    'form_submission_failed',
    'home_cta_clicked',
    'home_plan_modal_opened',
    'home_redirect_clicked',
    'home_redirect_started',
    'home_scroll_depth',
    'home_section_viewed',
    'home_viewed',
    'learning_summary_generated',
    'legal_page_viewed',
    'lesson_completed',
    'login',
    'logout',
    'page_view',
    'parent_link_approved',
    'parent_link_rejected',
    'parent_link_requested',
    'payment_failed',
    'progress_drilldown_opened',
    'purchase',
    'qna_completed',
    'qna_message_sent',
    'qna_scored',
    'qna_started',
    'quantity_updated',
    'quiz_completed',
    'quiz_question_answered',
    'quiz_scored',
    'quiz_started',
    'quiz_step_viewed',
    'rec_path_clicked',
    'rec_path_compute_failed',
    'rec_path_compute_succeeded',
    'rec_path_viewed',
    'refund',
    'search_abandoned',
    'search_result_clicked',
    'search_results_returned',
    'search_submitted',
    'settings_opened',
    'signup_completed',
    'signup_started',
    'study_plan_completed',
    'study_plan_create_failed',
    'study_plan_create_started',
    'study_plan_create_succeeded',
    'study_plan_deleted',
    'study_plan_reordered',
    'study_plan_revised',
    'subscription_canceled',
    'trial_started',
    'video_advanced',
    'video_completed',
    'video_feedback',
    'video_played',
    'video_progress',
    'video_saved',
    'video_unsaved',
]);

const EVENT_NAME_SET = new Set(EVENT_NAMES);

export function isKnownEventName(name) {
    return EVENT_NAME_SET.has(name);
}

// Canonical route_id values, registered as a custom dimension on the
// GA4 property so cross-app reports filter identically. From §D of the
// design doc.
export const ROUTE_IDS = Object.freeze({
    HOME: 'home',
    PROGRESS: 'progress',
    STUDY_PLANS: 'study_plans',
    STUDY_PLAN: 'study_plan',
    VIDEOS: 'videos',
    VIDEO: 'video',
    SAVED: 'saved',
    SEARCH: 'search',
    DASHBOARD: 'dashboard',
    CHILD_SETTINGS: 'child_settings',
    BILLING: 'billing',
    ACCOUNT: 'account',
    LOGIN: 'login',
    SIGNUP: 'signup',
    MARKETING_HOME: 'marketing_home',
    MARKETING_LEGAL: 'marketing_legal',
    OTHER: 'other',
});

// Match the *current* path to a canonical route_id. Falls back to
// ROUTE_IDS.OTHER for unmapped routes; the call site can pass
// `app_role` in to disambiguate (e.g. /progress under a child app vs.
// the parent dashboard variant).
export function routeIdForPath(pathname, { appRole = null } = {}) {
    if (!pathname || pathname === '/') {
        // App-shell roots: parent dashboard for lf_fe, child home for
        // test-yted. The caller passes app_role so we can distinguish.
        if (appRole === 'parent') return ROUTE_IDS.DASHBOARD;
        if (appRole === 'child') return ROUTE_IDS.HOME;
        return ROUTE_IDS.OTHER;
    }

    if (pathname === '/www_v1') {
        return ROUTE_IDS.MARKETING_HOME;
    }
    if (pathname.startsWith('/parent') && pathname.includes('progress')) {
        return ROUTE_IDS.PROGRESS;
    }

    const segments = pathname.split('/').filter(Boolean);
    const head = segments[0] || '';

    switch (head) {
        case 'parent':
            if (segments[1] === 'progress') return ROUTE_IDS.PROGRESS;
            if (segments[1] === 'study-plans' || segments[1] === 'study_plans') {
                return ROUTE_IDS.STUDY_PLANS;
            }
            if (segments[1] === 'study-plan' || segments[1] === 'study_plan') {
                return ROUTE_IDS.STUDY_PLAN;
            }
            if (segments[1] === 'children' && segments[2]) {
                return ROUTE_IDS.CHILD_SETTINGS;
            }
            return ROUTE_IDS.DASHBOARD;
        case 'progress':
            return ROUTE_IDS.PROGRESS;
        case 'study-plans':
        case 'study_plans':
            return ROUTE_IDS.STUDY_PLANS;
        case 'study-plan':
        case 'study_plan':
            return ROUTE_IDS.STUDY_PLAN;
        case 'videos':
        case 'feed':
            return ROUTE_IDS.VIDEOS;
        case 'video':
            return ROUTE_IDS.VIDEO;
        case 'saved':
            return ROUTE_IDS.SAVED;
        case 'search':
            return ROUTE_IDS.SEARCH;
        case 'login':
            return ROUTE_IDS.LOGIN;
        case 'signup':
        case 'student':
            return ROUTE_IDS.SIGNUP;
        case 'trial':
        case 'billing':
        case 'checkout':
            return ROUTE_IDS.BILLING;
        case 'account':
        case 'profile':
        case 'setting':
        case 'settings':
            return appRole === 'parent' ? ROUTE_IDS.ACCOUNT : ROUTE_IDS.CHILD_SETTINGS;
        case 'add_profile':
            return ROUTE_IDS.CHILD_SETTINGS;
        default:
            return ROUTE_IDS.OTHER;
    }
}

// Per-event allowed parameter shapes. Used by schema tests; not enforced
// at runtime (`track()` accepts any params and lets the adapter shape
// them). Each entry is the set of parameter names that *can* appear on
// the event — required ones are noted in `required`.
export const EVENT_SCHEMA = Object.freeze({
    home_section_viewed: { required: ['section'], optional: ['position_pct'] },
    home_cta_clicked: { required: ['cta'], optional: ['target_url', 'position'] },
    home_redirect_started: { required: ['destination'], optional: ['referrer'] },
    home_scroll_depth: { required: ['depth_pct'], optional: [] },
    legal_page_viewed: { required: ['page'], optional: [] },
    login: { required: [], optional: ['method', 'app_role'] },
    logout: { required: [], optional: ['app_role'] },
    signup_started: { required: ['step'], optional: ['app_role'] },
    signup_completed: { required: [], optional: ['via', 'app_role'] },
    checkout_started: { required: ['intent'], optional: ['child_count'] },
    purchase: { required: ['transaction_id', 'value', 'currency'], optional: ['child_count'] },
    trial_started: { required: ['value', 'currency'], optional: ['child_count'] },
    payment_failed: { required: ['reason'], optional: ['value', 'currency'] },
    subscription_canceled: { required: ['reason'], optional: ['tenure_days'] },
    refund: { required: ['transaction_id', 'value', 'currency'], optional: [] },
    quantity_updated: { required: ['from', 'to'], optional: [] },
    study_plan_create_started: { required: [], optional: ['goal_text_length', 'child_id', 'created_by_role'] },
    study_plan_create_succeeded: { required: ['study_plan_id'], optional: ['lesson_count', 'topic_count', 'gen_duration_ms', 'tokens_in', 'tokens_out', 'created_by_role'] },
    study_plan_create_failed: { required: ['reason'], optional: ['gen_duration_ms', 'created_by_role'] },
    study_plan_revised: { required: ['study_plan_id'], optional: ['revision_kind', 'lessons_changed', 'revised_by_role'] },
    study_plan_deleted: { required: ['study_plan_id'], optional: ['deleted_by_role'] },
    study_plan_reordered: { required: ['study_plan_id'], optional: ['reordered_by_role'] },
    video_played: { required: ['video_id', 'source'], optional: ['lesson_id', 'study_plan_id', 'search_query_id', 'rec_position', 'position_pct'] },
    video_progress: { required: ['video_id', 'source', 'position_pct'], optional: ['lesson_id', 'study_plan_id', 'search_query_id', 'rec_position'] },
    video_completed: { required: ['video_id', 'source'], optional: ['lesson_id', 'study_plan_id', 'search_query_id', 'rec_position', 'position_pct'] },
    video_advanced: { required: ['from_state', 'to_state'], optional: ['source'] },
    video_saved: { required: ['video_id'], optional: ['source'] },
    video_unsaved: { required: ['video_id'], optional: ['source'] },
    video_feedback: { required: ['video_id', 'sentiment'], optional: ['source'] },
    learning_summary_generated: { required: ['video_id'], optional: ['concepts_count', 'gen_duration_ms'] },
    lesson_completed: { required: ['lesson_id', 'study_plan_id'], optional: ['videos_completed'] },
    study_plan_completed: { required: ['study_plan_id'], optional: ['lessons_count', 'total_duration_days'] },
    rec_path_viewed: { required: ['lesson_id', 'study_plan_id'], optional: ['position'] },
    rec_path_clicked: { required: ['lesson_id', 'study_plan_id', 'video_id'], optional: ['position'] },
    rec_path_compute_succeeded: { required: ['lesson_id', 'study_plan_id'], optional: ['compute_duration_ms', 'candidate_count'] },
    rec_path_compute_failed: { required: ['lesson_id', 'study_plan_id', 'reason'], optional: [] },
    quiz_started: { required: ['video_id', 'chat_id'], optional: ['lesson_id', 'study_plan_id', 'question_count'] },
    quiz_step_viewed: { required: ['chat_id', 'step_index'], optional: ['is_multiple_choice', 'is_hint'] },
    quiz_question_answered: { required: ['chat_id', 'step_index', 'is_correct'], optional: ['attempt_index', 'concept_id'] },
    quiz_completed: { required: ['chat_id'], optional: ['step_count', 'ended_by'] },
    quiz_scored: { required: ['chat_id', 'score'], optional: ['score_source'] },
    qna_started: { required: ['video_id', 'chat_id'], optional: ['lesson_id', 'study_plan_id'] },
    qna_message_sent: { required: ['chat_id', 'message_index'], optional: [] },
    qna_completed: { required: ['chat_id'], optional: ['message_count', 'ended_by'] },
    qna_scored: { required: ['chat_id', 'score'], optional: ['score_source'] },
    search_submitted: { required: ['search_query_id'], optional: ['query_length', 'from_page'] },
    search_results_returned: { required: ['search_query_id', 'result_count'], optional: ['latency_ms', 'is_zero_result'] },
    search_result_clicked: { required: ['search_query_id', 'result_position', 'video_id'], optional: [] },
    search_abandoned: { required: ['search_query_id'], optional: ['had_results'] },
    dashboard_viewed: { required: [], optional: ['child_id'] },
    progress_drilldown_opened: { required: ['child_id', 'study_plan_id'], optional: [] },
    child_switched: { required: ['from_child_id', 'to_child_id'], optional: [] },
    settings_opened: { required: [], optional: ['section'] },
    account_deletion_requested: { required: [], optional: [] },
    account_deleted: { required: ['user_profile_id'], optional: [] },
    form_submission_failed: { required: ['form'], optional: ['field', 'reason'] },
    api_error: { required: ['route', 'status_code'], optional: ['code'] },
    feature_blocked: { required: ['feature', 'reason'], optional: [] },
    email_sent: { required: ['email_id', 'kind'], optional: ['recipient_role', 'template_version', 'provider_message_id'] },
    email_opened: { required: ['email_id', 'kind'], optional: ['recipient_role'] },
    email_link_clicked: { required: ['email_id', 'kind'], optional: ['link_id', 'recipient_role'] },
    child_account_created: { required: ['child_id'], optional: ['grade_level', 'under_13'] },
    parent_link_requested: { required: ['child_id'], optional: [] },
    parent_link_approved: { required: ['child_id'], optional: [] },
    parent_link_rejected: { required: ['child_id'], optional: ['reason'] },
    coppa_consent_granted: { required: ['child_id'], optional: ['via'] },
    coppa_consent_revoked: { required: ['child_id'], optional: [] },
    home_viewed: { required: [], optional: [] },
    home_plan_modal_opened: { required: ['plan_subject'], optional: ['position'] },
    home_redirect_clicked: { required: ['destination'], optional: ['referrer'] },
    page_view: { required: [], optional: ['route_id', 'lesson_id', 'study_plan_id'] },
});

// Email kind enum, for parameter `kind` on email_* events.
export const EMAIL_KINDS = Object.freeze([
    'verification',
    'password_reset',
    'summary',
    'parent_link_request',
    'coppa_consent_request',
    'coppa_consent_revoked',
    'trial_expiring',
    'subscription_canceled',
    'account_deletion',
    'welcome',
]);

// Video source enum (`source` parameter on video_* events).
export const VIDEO_SOURCES = Object.freeze([
    'study_plan',
    'rec_path',
    'search',
    'saved',
    'all_videos',
    'home',
    'direct',
]);

// Conversion events to mark as conversions in GA4.
export const CONVERSION_EVENTS = Object.freeze([
    'signup_completed',
    'purchase',
    'trial_started',
    'study_plan_completed',
    'lesson_completed',
]);

// Custom dimensions registered on the property.
export const CUSTOM_DIMENSIONS = Object.freeze([
    { parameter_name: 'app_role', scope: 'USER', display_name: 'App role' },
    { parameter_name: 'subscription_tier', scope: 'USER', display_name: 'Subscription tier' },
    { parameter_name: 'subscription_status', scope: 'USER', display_name: 'Subscription status' },
    { parameter_name: 'child_count', scope: 'USER', display_name: 'Child count' },
    { parameter_name: 'grade_level', scope: 'USER', display_name: 'Grade level' },
    { parameter_name: 'account_age_days', scope: 'USER', display_name: 'Account age (days)' },
    { parameter_name: 'consent_status', scope: 'USER', display_name: 'Consent status' },
    { parameter_name: 'route_id', scope: 'EVENT', display_name: 'Route ID' },
    { parameter_name: 'source', scope: 'EVENT', display_name: 'Video source surface' },
    { parameter_name: 'environment', scope: 'EVENT', display_name: 'Build environment' },
]);
